import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { encounterQueryKeys, encountersApi } from "../../../shared/api/encounters";
import { externalSlipQueryKeys, externalSlipsApi } from "../../../shared/api/externalSlips";
import { describeDose, doseDurationDays } from "../../../shared/dose";
import type {
  EncounterAttachment,
  EncounterDetail,
  EncounterPrescriptionItem,
  EncounterSummary,
} from "../../../shared/types/encounter";
import type { ExternalSlipView } from "../../../shared/types/externalSlip";
import { ErrorState } from "../../../shared/ui/ErrorState";
import { Skeleton } from "../../../shared/ui/Skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  AttachmentTile,
  SlipHtml,
  resultSections,
} from "../../appointments/components/EncounterResultCard";

interface Props {
  /** Lần khám đang xem (dòng của Lịch sử khám); `null` = hộp thoại đóng. */
  encounter: EncounterSummary | null;
  onClose: () => void;
}

/**
 * Hộp thoại "Kết quả của: <bệnh nhân>" — bản HIS của `ModalDetailResult` trên SalePro
 * (`OrderForServicePage/components/ModalDetailResult`), mở từ cột "Kết quả" của Lịch sử khám.
 *
 * <p>Cùng bố cục và thứ tự khối như SalePro: ô ảnh/tài liệu → "Chẩn đoán:" → "Đơn thuốc:" →
 * "Kết quả:", mỗi khối cách nhau một đường kẻ, chân là nút "Đóng"; không có gì thì "Chưa có kết
 * quả". Nội dung lấy đúng như thẻ "Kết quả & Tài liệu" trên Chi tiết đặt lịch: khi lần khám có
 * phiếu MeetDoctor (migrate từ SalePro hoặc quét QR) thì Đơn thuốc và Kết quả hiện NGUYÊN HTML
 * phiếu — đó chính là thứ SalePro hiện trong `examResult.prescription` / `examResult.result`;
 * chỉ lần khám gõ trực tiếp trong HIS mới dựng từ bảng thuốc và bản SOAP.
 *
 * <p>Hai chỗ từng làm hộp thoại giật: (1) lúc ĐÓNG, `encounter` về `null` ngay khi hoạt ảnh
 * thu nhỏ còn chạy ~200ms, nên thân hộp đổi thành ô lỗi và co lại giữa chừng — vì thế lần khám
 * cuối được giữ lại (`shown`) cho tới khi hộp gỡ hẳn; (2) lúc MỞ, khung xương thấp rồi nhảy lên
 * gấp đôi khi dữ liệu về — vì thế danh sách gọi `usePrefetchEncounterResult` khi rê chuột /
 * focus vào "Xem kết quả", để lúc bấm thường đã có dữ liệu trong cache.
 */
export function EncounterResultDialog({ encounter, onClose }: Props) {
  const open = encounter !== null;
  // Giữ lần khám cuối cùng để nội dung không đổi trong lúc hoạt ảnh đóng còn chạy.
  const last = useRef<EncounterSummary | null>(null);
  if (encounter) last.current = encounter;
  const shown = encounter ?? last.current;
  const id = shown?.id ?? "";
  const appointmentId = shown?.appointmentId ?? null;
  const detail = useQuery({
    queryKey: encounterQueryKeys.detail(id),
    queryFn: () => encountersApi.get(id),
    enabled: open,
  });
  const slips = useQuery({
    queryKey: externalSlipQueryKeys.forAppointment(appointmentId ?? ""),
    queryFn: () => externalSlipsApi.forAppointment(appointmentId ?? ""),
    enabled: open && !!appointmentId,
  });
  const [preview, setPreview] = useState<{ att: EncounterAttachment; url: string } | null>(null);
  const loading = detail.isLoading || (!!appointmentId && slips.isLoading);
  // Khi mở, focus vào vùng cuộn thay vì nút X (phần tử bấm được đầu tiên): mở bằng chuột không
  // làm nút X sáng ring, và phím mũi tên / PageDown cuộn nội dung được ngay. Tab vẫn tới nút X.
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      {/* Tiêu đề và chân (nút X, nút Đóng) đứng yên; chỉ phần thân cuộn — nên `overflow` nằm ở thân,
          không ở DialogContent, vì nút X là `absolute` theo DialogContent và sẽ trôi theo nếu chính
          nó là vùng cuộn. */}
      <DialogContent
        className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-[75vw] xl:max-w-[1100px]"
        data-testid="encounter-result-dialog"
        hideClose
        onOpenAutoFocus={(ev) => {
          ev.preventDefault();
          scrollRef.current?.focus();
        }}
      >
        {/* Nút đóng chuẩn (`DialogCloseButton`) đặt trong hàng tiêu đề thay vì nút `absolute` mặc định,
            để căn giữa theo chiều cao tiêu đề; `-mr-2` cho icon thẳng mép với nội dung (px-6). */}
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between gap-4 space-y-0 border-b border-border px-6 py-3">
          <div className="min-w-0">
            <DialogTitle className="text-lg font-normal">
              Kết quả của:{" "}
              <span className="font-medium text-primary">
                {detail.data?.patientName ?? shown?.patientName ?? "…"}
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Ảnh, chẩn đoán, đơn thuốc và kết quả của lần khám.
            </DialogDescription>
          </div>
          <DialogCloseButton className="-mr-2" data-testid="result-close-x" />
        </DialogHeader>

        <div
          ref={scrollRef}
          tabIndex={-1}
          className="min-h-0 flex-1 overflow-y-auto px-6 py-4 outline-none"
          data-testid="result-scroll"
        >
          {loading ? (
            <ResultSkeleton />
          ) : detail.isError || !detail.data ? (
            <ErrorState error={detail.error} />
          ) : (
            <ResultBody
              encounter={detail.data}
              slip={slips.data?.[0] ?? null}
              onPreview={(att, url) => setPreview({ att, url })}
            />
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border px-6 py-3">
          <Button type="button" variant="secondary" onClick={onClose} data-testid="result-close">
            Đóng
          </Button>
        </DialogFooter>

        <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="truncate text-base">{preview?.att.fileName}</DialogTitle>
            </DialogHeader>
            {preview && (
              <img
                src={preview.url}
                alt={preview.att.fileName}
                className="max-h-[75vh] w-full rounded-md object-contain"
              />
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Nạp sẵn dữ liệu của hộp thoại (chi tiết lần khám + phiếu ngoài) — gọi khi rê chuột / focus
 * vào "Xem kết quả". Dùng đúng khoá và `staleTime` mặc định, nên lúc bấm `useQuery` thấy cache
 * và vẽ nội dung ngay, không qua khung xương rồi nhảy cỡ.
 */
export function usePrefetchEncounterResult() {
  const qc = useQueryClient();
  return (e: EncounterSummary) => {
    void qc.prefetchQuery({
      queryKey: encounterQueryKeys.detail(e.id),
      queryFn: () => encountersApi.get(e.id),
    });
    if (e.appointmentId) {
      const appointmentId = e.appointmentId;
      void qc.prefetchQuery({
        queryKey: externalSlipQueryKeys.forAppointment(appointmentId),
        queryFn: () => externalSlipsApi.forAppointment(appointmentId),
      });
    }
  };
}

/**
 * Khung xương theo đúng hình khối sẽ hiện (chẩn đoán → đơn thuốc → kết quả) và cao gần bằng nội
 * dung thường gặp, để khi dữ liệu về hộp thoại không nhảy cỡ và dời tâm.
 */
function ResultSkeleton() {
  return (
    <div className="grid gap-3" data-testid="result-skeleton" aria-busy="true">
      <Skeleton width="45%" height={18} />
      <Separator />
      <Skeleton width={110} height={18} />
      <Skeleton height={150} />
      <Separator />
      <Skeleton width={90} height={18} />
      <Skeleton height={150} />
    </div>
  );
}

/** Nhãn khối như SalePro: chữ xanh, đậm, cỡ 16px. */
function Label({ children }: { children: string }) {
  return <p className="m-0 mb-2 text-base font-semibold text-primary">{children}</p>;
}

function doseSummary(it: EncounterPrescriptionItem): string {
  const days = doseDurationDays(it);
  return [describeDose(it), days ? `${days} ngày` : null].filter(Boolean).join(" · ");
}

function ResultBody({
  encounter: e,
  slip,
  onPreview,
}: {
  encounter: EncounterDetail;
  slip: ExternalSlipView | null;
  onPreview: (att: EncounterAttachment, url: string) => void;
}) {
  const hasSlipRx = !!slip?.prescriptionHtml;
  const hasSlipResult = !!slip?.labResultHtml;
  // Phiếu ngoài đã mang ảnh trong HTML kết quả (như SalePro) thì dải ảnh chỉ còn tệp không phải ảnh.
  const attachments = (hasSlipResult
    ? e.attachments.filter((a) => !a.mimeType.startsWith("image/"))
    : e.attachments
  ).filter((a) => a.downloadable);
  const primary = e.diagnoses.find((d) => d.primary) ?? e.diagnoses[0];
  const diagnoses = primary ? [primary, ...e.diagnoses.filter((d) => d !== primary)] : [];
  const diagnosisText = diagnoses.length === 0 ? (slip?.diagnosis ?? null) : null;
  const rx = e.prescription.items;
  const sections = hasSlipResult ? [] : resultSections(e.soap);
  const hasRx = hasSlipRx || rx.length > 0;
  const hasResult = hasSlipResult || sections.length > 0;
  const hasDx = diagnoses.length > 0 || !!diagnosisText;

  if (attachments.length === 0 && !hasDx && !hasRx && !hasResult) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground" data-testid="result-empty">
        Chưa có kết quả
      </p>
    );
  }

  return (
    <div className="grid gap-3 text-sm">
      {attachments.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="result-attachments">
            {attachments.map((a) => (
              <AttachmentTile key={a.id} encounterId={e.id} att={a} onPreview={onPreview} />
            ))}
          </div>
          <Separator />
        </>
      )}

      {hasDx && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2" data-testid="result-diagnoses">
            <span className="text-base font-semibold text-primary">Chẩn đoán:</span>
            {diagnoses.length > 0 ? (
              <ul className="m-0 list-none p-0 text-base">
                {diagnoses.map((d) => (
                  <li key={d.id} className="inline [&:not(:last-child)]:after:content-['_·_']">
                    {d.label}
                    {d.icd10Code && (
                      <span className="ml-1 font-mono text-xs text-muted-foreground">({d.icd10Code})</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-base">{diagnosisText}</span>
            )}
          </div>
          <Separator />
        </>
      )}

      {hasSlipRx && (
        <div>
          <Label>Đơn thuốc:</Label>
          <SlipHtml html={slip!.prescriptionHtml!} testId="slip-prescription" />
        </div>
      )}
      {!hasSlipRx && rx.length > 0 && (
        <div>
          <Label>Đơn thuốc:</Label>
          <div className="overflow-x-auto rounded-md border border-border bg-muted/20 p-3">
            <table className="w-full border-collapse text-sm" data-testid="result-prescription">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">Thuốc</th>
                  <th className="py-1 pr-3 text-right font-medium">SL</th>
                  <th className="py-1 font-medium">Cách dùng</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rx.map((it) => (
                  <tr key={it.id} className="align-top">
                    <td className="py-1.5 pr-3 font-medium text-foreground">
                      {it.medicineName}
                      {it.medicineStrength ? ` ${it.medicineStrength}` : ""}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">
                      {it.quantity} {it.medicineUnit}
                    </td>
                    <td className="py-1.5 text-muted-foreground">{doseSummary(it)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasResult && (
        <>
          {hasRx && <Separator />}
          <div data-testid="result-outcome">
            <Label>Kết quả:</Label>
            {hasSlipResult ? (
              <SlipHtml html={slip!.labResultHtml!} testId="slip-lab-result" />
            ) : (
              <div className="grid gap-2 rounded-md border border-border bg-muted/20 p-3">
                {sections.map((s) => (
                  <div key={s.label}>
                    <span className="font-medium">{s.label}:</span>{" "}
                    <span className="whitespace-pre-line">{s.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Separator />
        </>
      )}
    </div>
  );
}
